import {
  BEFAST_ITEMS,
  befastPositive,
  CINCINNATI_ITEMS,
  interpretCincinnati,
  interpretLams,
  interpretNihss,
  interpretRace,
  LAMS_ITEMS,
  NIHSS_ITEMS,
  raceItems,
  scoreNihss,
  strokeWindows,
  sumScore,
  type BefastFindings,
  type CincinnatiFindings,
  type HemiparesisSide,
  type ScoreAnswers,
} from '@ems/clinical';
import { Clock, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge, Button, Field, Input, Segmented } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ResultPanel, Section, ToolPage } from '../common';
import { ScoreItems } from './ScoreTools';

type Tab = 'befast' | 'cincinnati' | 'lams' | 'race' | 'nihss';

function toLocalInput(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function YesNo({ value, onChange, yes = 'Yes', no = 'No' }: { value: boolean | undefined; onChange: (v: boolean) => void; yes?: string; no?: string }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[false, true].map((v) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'h-11 rounded-lg border text-sm font-semibold',
            value === v
              ? v
                ? 'border-red-500 bg-red-50 text-red-800 dark:bg-red-500/15 dark:text-red-200'
                : 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200'
              : 'border-stone-200 dark:border-stone-700',
          )}
        >
          {v ? yes : no}
        </button>
      ))}
    </div>
  );
}

function LastKnownWell() {
  const [lkw, setLkw] = useState('');
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const date = lkw ? new Date(lkw) : null;
  const windows = date && !Number.isNaN(date.getTime()) ? strokeWindows(date, now) : null;
  const future = date && date.getTime() > now.getTime();
  const h = windows ? Math.floor(windows.elapsedMinutes / 60) : 0;
  const m = windows ? Math.round(windows.elapsedMinutes % 60) : 0;

  return (
    <Section title="Last known well" action={<Button size="sm" variant="outline" icon={<Clock className="size-4" />} onClick={() => setLkw(toLocalInput(new Date()))}>Now</Button>}>
      <Field label="Date and time the patient was last at baseline">
        <Input type="datetime-local" value={lkw} max={toLocalInput(now)} onChange={(e) => setLkw(e.target.value)} className="h-12" />
      </Field>
      {future && <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">Last known well cannot be in the future.</p>}
      {windows && !future && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums">
            {h} h {m} min
          </span>
          <Badge tone={windows.withinThrombolyticWindow ? 'success' : 'neutral'}>{windows.withinThrombolyticWindow ? 'Within 4.5 h' : 'Beyond 4.5 h'}</Badge>
          <Badge tone={windows.withinThrombectomyWindow ? 'brand' : 'neutral'}>{windows.withinThrombectomyWindow ? 'Within 24 h' : 'Beyond 24 h'}</Badge>
        </div>
      )}
      <p className="mt-2 text-xs text-stone-500">Treatment windows are determined by the receiving stroke team. Follow your regional destination plan.</p>
    </Section>
  );
}

export default function StrokeTool() {
  const [tab, setTab] = useState<Tab>('befast');
  const [befast, setBefast] = useState<BefastFindings>({});
  const [cincinnati, setCincinnati] = useState<CincinnatiFindings>({});
  const [lams, setLams] = useState<ScoreAnswers>({});
  const [raceSide, setRaceSide] = useState<HemiparesisSide>('right');
  const [race, setRace] = useState<ScoreAnswers>({});
  const [nihss, setNihss] = useState<ScoreAnswers>({});

  const reset = () => {
    setBefast({});
    setCincinnati({});
    setLams({});
    setRace({});
    setNihss({});
  };

  const lamsResult = sumScore(LAMS_ITEMS, lams);
  const raceResult = sumScore(raceItems(raceSide), race);
  const nihssResult = scoreNihss(nihss);
  const answeredNihss = NIHSS_ITEMS.filter((i) => nihss[i.id] !== undefined).length;

  return (
    <ToolPage
      id="stroke"
      actions={
        <Button variant="ghost" size="sm" icon={<RotateCcw className="size-4" />} onClick={reset}>
          Reset all
        </Button>
      }
    >
      <LastKnownWell />
      <div className="scrollbar-none overflow-x-auto">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'befast', label: 'BE-FAST' },
            { value: 'cincinnati', label: 'Cincinnati' },
            { value: 'lams', label: 'LAMS' },
            { value: 'race', label: 'RACE' },
            { value: 'nihss', label: 'NIHSS' },
          ]}
        />
      </div>

      {tab === 'befast' && (
        <>
          {Object.keys(befast).length > 0 && (
            <ResultPanel
              label="BE-FAST screen"
              value={befastPositive(befast) ? 'Positive' : Object.keys(befast).length === BEFAST_ITEMS.length ? 'Negative' : 'In progress'}
              severity={befastPositive(befast) ? 'critical' : Object.keys(befast).length === BEFAST_ITEMS.length ? 'normal' : undefined}
              detail={befastPositive(befast) ? 'Check glucose, establish last known well, and perform an LVO scale.' : undefined}
            />
          )}
          {BEFAST_ITEMS.map((item) => (
            <Section key={item.id} title={`${item.letter} — ${item.label}`}>
              <p className="-mt-2 mb-2 text-sm text-stone-600 dark:text-stone-300">{item.prompt}</p>
              <YesNo value={befast[item.id]} onChange={(v) => setBefast({ ...befast, [item.id]: v })} />
            </Section>
          ))}
        </>
      )}

      {tab === 'cincinnati' && (
        <>
          {Object.keys(cincinnati).length > 0 && (
            <ResultPanel
              label="Cincinnati Prehospital Stroke Scale"
              value={`${interpretCincinnati(cincinnati).abnormal} abnormal`}
              severity={interpretCincinnati(cincinnati).positive ? 'critical' : 'normal'}
              detail={interpretCincinnati(cincinnati).detail}
            />
          )}
          {CINCINNATI_ITEMS.map((item) => (
            <Section key={item.id} title={item.label}>
              <div className="-mt-2 mb-2 grid gap-1 text-sm text-stone-600 dark:text-stone-300">
                <span>Normal: {item.normal}</span>
                <span>Abnormal: {item.abnormal}</span>
              </div>
              <YesNo value={cincinnati[item.id]} onChange={(v) => setCincinnati({ ...cincinnati, [item.id]: v })} yes="Abnormal" no="Normal" />
            </Section>
          ))}
        </>
      )}

      {tab === 'lams' && (
        <>
          <ResultPanel
            label="Los Angeles Motor Scale"
            value={lamsResult.complete ? lamsResult.total : '—'}
            unit={lamsResult.complete ? '/ 5' : 'incomplete'}
            severity={lamsResult.complete ? interpretLams(lamsResult.total).severity : undefined}
            interpretation={lamsResult.complete ? interpretLams(lamsResult.total).label : undefined}
          />
          <ScoreItems items={LAMS_ITEMS} answers={lams} onChange={setLams} />
        </>
      )}

      {tab === 'race' && (
        <>
          <Segmented
            value={raceSide}
            onChange={(s) => {
              setRaceSide(s);
              setRace({ ...race, cortical: undefined });
            }}
            options={[
              { value: 'right', label: 'Right-sided weakness' },
              { value: 'left', label: 'Left-sided weakness' },
            ]}
          />
          <ResultPanel
            label="RACE scale"
            value={raceResult.complete ? raceResult.total : '—'}
            unit={raceResult.complete ? '/ 9' : 'incomplete'}
            severity={raceResult.complete ? interpretRace(raceResult.total).severity : undefined}
            interpretation={raceResult.complete ? interpretRace(raceResult.total).label : undefined}
          />
          <ScoreItems items={raceItems(raceSide)} answers={race} onChange={setRace} />
        </>
      )}

      {tab === 'nihss' && (
        <>
          <div className="sticky top-14 z-20 -mx-1 bg-stone-50/95 px-1 py-2 backdrop-blur dark:bg-ink-950/95">
            <ResultPanel
              label={`NIH Stroke Scale — ${answeredNihss}/${NIHSS_ITEMS.length} items`}
              value={nihssResult.total}
              unit="/ 42"
              severity={nihssResult.complete ? interpretNihss(nihssResult.total).severity : undefined}
              interpretation={nihssResult.complete ? interpretNihss(nihssResult.total).label : 'Complete all items for a final score'}
              detail={nihssResult.untestable.length > 0 ? `Untestable items (scored 0): ${nihssResult.untestable.join(', ')}` : undefined}
            />
          </div>
          <ScoreItems items={NIHSS_ITEMS} answers={nihss} onChange={setNihss} untestableLabel="UN — amputation, joint fusion, or intubated" />
        </>
      )}
    </ToolPage>
  );
}
